import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {useLocalization} from "../../../../hooks/localization"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import {useSelector} from "react-redux"
import {useRPC} from "../../../../hooks/rpc"
import {useMessage} from "../../../../hooks/message"
import {useQuestion} from "../../../../hooks/question"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../../../components/Select"
import {hasAuthority} from "../../../../utils/role"
import {UserRole} from "../../../../types/control"

export const AdvancedPanelSettings = memo(function AdvancedPanelSettings() {
    const {t} = useLocalization()
    const mousePointerStatus = useSelector(state => state.control.mousePointerStatus)
    const externalUsbStatus = useSelector(state => state.control.externalUsbStatus)
    const {success, failure} = useMessage()
    const screenRotation = useSelector(state => state.control.workerConfig?.panel?.rotation)
    const question = useQuestion()
    const [rpc] = useRPC()
    const [mousePointer, setMousePointer] = useState(mousePointerStatus || false)
    const [externalUsb, setExternalUsb] = useState(externalUsbStatus || false)
    const role = useSelector(state => state.ui.role)
    const allowedRole = useCallback((select: UserRole) => hasAuthority(role, select), [role])

    const rotateTypeList = useMemo<SelectItem<string>[]>(
        () => [
            {
                value: "0",
                label: "0°"
            },
            {
                value: "90",
                label: "90°"
            },
            {
                value: "180",
                label: "180°"
            },
            {
                value: "270",
                label: "270°"
            }
        ],
        []
    )

    const [selectedRotate, setSelectedRotate] = useState<SelectItem<string>>(
        screenRotation == null
            ? rotateTypeList[0]
            : rotateTypeList.find(screenRotate => screenRotate.value === screenRotation)
    )

    const onChangeMousePointer = useCallback(() => {
        question(t("the_window_will_reload"), () => {
            let newStatus = mousePointer
            if (newStatus) {
                newStatus = false
            } else {
                newStatus = true
            }
            setMousePointer(newStatus)
            rpc.updateMouseCursorVisibility(newStatus).catch(failure)
        })
    }, [t, question, failure, rpc, mousePointer])

    const onChangeExternalUsb = useCallback(() => {
        let newStatus = externalUsb
        if (newStatus) {
            newStatus = false
        } else {
            newStatus = true
        }
        setExternalUsb(newStatus)
        rpc.updateUsbConnectionStatus(newStatus).catch(failure)
        if (!newStatus) {
            success(t("reconnect_the_device"))
        }
    }, [t, failure, success, rpc, externalUsb])

    useEffect(() => {
        if (selectedRotate?.value !== screenRotation) {
            rpc.updateRotateType(selectedRotate?.value).catch(failure)
        }
    }, [failure, rpc, screenRotation, selectedRotate])

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={[]}
                className="service-edit"
                backUrl={"/settings/advanced-settings"}
                style={null}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        {renderIf(allowedRole("developer"))(() => (
                            <>
                                <SettingCheckBoxComponent
                                    label={t("mouse_pointer")}
                                    set={onChangeMousePointer}
                                    value={mousePointer}
                                />
                                <SettingCheckBoxComponent
                                    label={t("external_usb")}
                                    set={onChangeExternalUsb}
                                    value={externalUsb}
                                />
                            </>
                        ))}
                        {renderIf(allowedRole("service"))(() => (
                            <>
                                <SettingSelectComponent
                                    label={t("customizations.screen_direction")}
                                    options={rotateTypeList}
                                    value={selectedRotate}
                                    onChange={setSelectedRotate}
                                />
                            </>
                        ))}
                    </div>
                    <div className="content-wrapper"></div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
